/**
 * OpsRetrievalFallbackDialog
 *
 * Shown when Google Patents is unavailable during full-text download.
 * Offers to continue with OPS (slower) or cancel.
 * Shared by OpsRetrievalModal and CpcDownloadModal.
 */

import { useTranslation } from 'react-i18next';
import { formatDuration } from '../services/opsRetrievalService';

interface OpsRetrievalFallbackDialogProps {
  estimatedFullSeconds: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function OpsRetrievalFallbackDialog({
  estimatedFullSeconds,
  onConfirm,
  onCancel,
}: OpsRetrievalFallbackDialogProps) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-800">
          {t('opsRetrieval.fallback.title', 'Google Patents unavailable')}
        </h3>
        <p className="text-sm text-slate-600">
          {t(
            'opsRetrieval.fallback.message',
            'The fast download service (Google Patents) is currently unavailable. You can continue using EPO OPS (slower).',
          )}
        </p>
        <p className="text-xs text-slate-500">
          {t('opsRetrieval.fallback.estimate', 'Estimated duration via OPS:')} ~{formatDuration(estimatedFullSeconds)}
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
            onClick={onCancel}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={onConfirm}
          >
            {t('opsRetrieval.fallback.continueOps', 'Continue with OPS')}
          </button>
        </div>
      </div>
    </div>
  );
}
